import { useContext } from 'react';
import { Field } from 'formik';
import { TextField } from 'formik-material-ui';
import { Switch } from '../../../../common/formik';
import { Grid, Typography, Card, CardContent, CardHeader, Divider } from '@material-ui/core';
import { Select, InputFiles, FileList, FieldError } from '../../../../common';
import { PayerForm } from './PayerForm';
import { CustomerRequestContext } from '../../CustomerRequestContext';
import { TabsContext } from '../TabsContext';
import { SettingsContext } from '../../../../contexts';
import { SapTableNames, AppSettingNames, RequestStatus } from '../../../../../helpers';
import { DbType } from '../../../../../helpers/Common';

export const ConditionsForm = (props) => {
    const {
        state,
        getTableOptionsWithCode
    } = useContext(CustomerRequestContext)

    const {
        values,
        setFieldValue,
        isSavingRequest
    } = useContext(TabsContext);

    const { appSettingsECC, appSettingsS4 } = useContext(SettingsContext);

    const italyCountrySetting = values.dbType == DbType.ECC ?
        appSettingsECC.find(x => x.appSettingKey === AppSettingNames.ItalyCountryId) :
        appSettingsS4.find(x => x.appSettingKey === AppSettingNames.ItalyCountryId);

    const italyCountryId = italyCountrySetting ? parseInt(italyCountrySetting.value) : 0;

    const fileUploadedHandler = (storedFile) => {
        const docs = [].concat(values.order.attachments || []);
        docs.push({ ...storedFile });
        // We need to set the value manually
        // otherwise setFieldValue will not work property
        values.order.attachments = docs;
        setFieldValue("order.attachments", docs);
    }

    const fileRemovedHandler = (storedFile) => {
        const docs = values.order.attachments.filter(x => x.storedFileId !== storedFile.storedFileId);
        // We need to set the value manually
        // otherwise setFieldValue will not work property
        values.order.attachments = docs;
        setFieldValue("order.attachments", docs);
    }

    return (
        <Card>
            <CardHeader title="Standard conditions" />
            <CardContent>
                <Grid container spacing={3}>
                    <Grid item xl={6} md={12} xs={12}>
                        <Select
                            name="invoiceCurrencyId"
                            label="Invoice currency *"
                            disabled={props.disabled}
                            options={getTableOptionsWithCode(SapTableNames.Currencies, values.dbType)}>
                        </Select>
                        <FieldError name="invoiceCurrencyId" errors={state.errors} />
                    </Grid>
                    <Grid item xl={6} md={12} xs={12}>
                        <Select
                            name="order.paymentTermId"
                            label="Payment terms (subject to Credit Risk approval/review) *"
                            disabled={props.disabled || state.request.status === RequestStatus.AccountReceivableApproval}
                            options={getTableOptionsWithCode(SapTableNames.PaymentTerms, values.dbType)}
                        />
                        <FieldError name="order.paymentTermId" errors={state.errors} />
                    </Grid>
                    <Grid item xl={12} xs={12}>
                        <Field
                            name="order.specialConditions"
                            type="text"
                            style={{ width: "100%" }}
                            variant="outlined"
                            label="Special conditions, investments, shared costs and notes *"
                            disabled={props.disabled}
                            component={TextField}
                        />
                        <FieldError name="order.specialConditions" errors={state.errors} />
                    </Grid>
                    <Grid item xl={12} xs={12}>
                        <Field
                            label="Letter of Credit/guarantee"
                            name="order.hasCreditLetter"
                            disabled={isSavingRequest || props.disabled}
                            component={Switch}
                        />
                        <FieldError name="order.hasCreditLetter" errors={state.errors} />
                    </Grid>
                    <Divider style={{ width: "100%" }} />
                    <Grid item xl={12} md={12} xs={12}>
                        <FileList
                            title="Attachments"
                            files={values.order ? values.order.attachments : []}
                            disabled={props.disabled}
                            onFileRemoved={fileRemovedHandler}
                        />
                        <FieldError name="order.attachments" errors={state.errors} />
                    </Grid>

                    {
                        !props.disabled &&
                        <Grid item xl={12} md={12} xs={12}>
                            <InputFiles
                                title="Upload attachments"
                                onFileUploaded={fileUploadedHandler}
                            />
                        </Grid>
                    }

                    <Divider style={{ width: "100%" }} />
                    <Grid item xl={12} md={12} xs={12}>
                        <Field
                            label="Is payer different from client name?"
                            name="order.isPayerDifferentFromClient"
                            disabled={isSavingRequest || props.disabled}
                            component={Switch}
                        />
                        <FieldError name="order.isPayerDifferentFromClient" errors={state.errors} />
                    </Grid>

                    {
                        values.order.isPayerDifferentFromClient &&
                        <>
                            <Grid item xl={12} xs={12} md={12}>
                                <Typography variant="subtitle1">
                                    Payer
                                </Typography>
                            </Grid>
                            <Grid item xl={12} md={12} xs={12}>
                                <PayerForm
                                    hasIBAN
                                    hasSwiftBic
                                    hasSDICode={values.order.payer.address.countryId === italyCountryId}
                                    formSuffixName="order.payer"
                                    disabled={props.disabled} />
                            </Grid>
                        </>
                    }

                    <Divider style={{ width: "100%" }} />

                    <Grid item xl={12} md={12} xs={12}>
                        <Field
                            label="Is invoice address different from client's address?"
                            name="order.isBillingDifferentFromClient"
                            disabled={isSavingRequest || props.disabled}
                            component={Switch}
                        />
                        <FieldError name="order.isBillingDifferentFromClient" errors={state.errors} />
                    </Grid>

                    {
                        values.order.isBillingDifferentFromClient &&
                        <>
                            <Grid item xl={12} xs={12} md={12}>
                                <Typography variant="subtitle1">
                                    Billing
                                </Typography>
                            </Grid>
                            <Grid item xl={12} md={12} xs={12}>
                                <PayerForm
                                    hasSDICode={values.order.billing.address.countryId === italyCountryId}
                                    formSuffixName="order.billing"
                                    disabled={props.disabled} />
                            </Grid>
                        </>
                    }

                    <Divider style={{ width: "100%" }} />

                    <Grid item xl={12} md={12} xs={12}>
                        <Field
                            label="Is there a Multibranch Contract"
                            name="order.hasMultiBranchContract"
                            disabled={isSavingRequest || props.disabled}
                            component={Switch}
                        />
                        <FieldError name="order.hasMultiBranchContract" errors={state.errors} />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};